import { useRef, useState } from 'react';
import { DsButton } from '../ds';
import { AlertIcon, CheckIcon, FolderIcon, UploadIcon } from '../ds/icons';
import { ApiV2Error } from '../../utils/apiV2';
import { trackEvent, AmplitudeEvent } from '../../utils/amplitude';
import {
  folderFromDrop,
  folderFromInput,
  folderProblem,
  PickedFolder,
  UploadedFolder,
  uploadFolder,
  UploadProgress,
} from '../../utils/folderUpload';

/**
 * The Upload source: a folder from this computer, dragged in or picked.
 *
 * Nothing is sent when the folder is chosen. Next hashes every file, sends
 * only the ones this account does not have yet — a second deploy of the same
 * project sends just what changed — and finalizes, so the configure step
 * starts from an upload that is complete and can be detected like a
 * repository.
 */

export interface UploadSourceValue {
  folder: PickedFolder;
  uploaded?: UploadedFolder;
}

interface UploadSourceProps {
  value: UploadSourceValue | null;
  onChange: (value: UploadSourceValue | null) => void;
  onNext: (uploaded: UploadedFolder) => void | Promise<void>;
  /** New code for this existing app, instead of the code of a new one. */
  appId?: string;
  /** The button that sends the folder. */
  nextLabel?: string;
  /** Tighter padding when nested in another card. */
  compact?: boolean;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const progressLabel = (progress: UploadProgress) => {
  if (progress.phase === 'hashing') return 'Reading files';
  if (progress.phase === 'finalizing') return 'Finishing';
  if (!progress.filesToSend) return 'Nothing new to send';
  return `Sending ${progress.filesToSend} new ${progress.filesToSend === 1 ? 'file' : 'files'}`;
};

// A directory picker is not in the DOM typings.
const directoryInputProps = { webkitdirectory: '', directory: '' } as Record<string, string>;

const UploadSource = ({
  value,
  onChange,
  onNext,
  appId,
  nextLabel = 'Next',
  compact = false,
}: UploadSourceProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  // Going on with a folder already sent (after the step after it failed).
  const [isContinuing, setIsContinuing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const folder = value?.folder;
  const problem = folder ? folderProblem(folder) : null;
  const isUploading = !!progress;

  const pick = (picked: PickedFolder) => {
    setError(null);
    onChange({ folder: picked });
    trackEvent(AmplitudeEvent.UPLOAD_FOLDER_SELECTED, {
      files: picked.files.length,
      bytes: picked.totalBytes,
      ignored: picked.ignoredCount,
    });
  };

  const onDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    if (isUploading) return;

    setIsReading(true);
    try {
      pick(await folderFromDrop(event.dataTransfer));
    } catch (e) {
      console.error('could not read the dropped folder', e);
      setError('That folder could not be read. Try choosing it with the button instead.');
    } finally {
      setIsReading(false);
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length) {
      pick(folderFromInput(event.target.files));
    }
    // Picking the same folder again must fire change again.
    event.target.value = '';
  };

  const onClickNext = async () => {
    if (!folder || problem || isUploading || isContinuing) return;

    // Already sent and nothing changed since: straight on. Busy like an
    // upload, so a second click can't start the next step twice.
    if (value?.uploaded) {
      setError(null);
      setIsContinuing(true);
      try {
        await onNext(value.uploaded);
      } finally {
        setIsContinuing(false);
      }
      return;
    }

    setError(null);
    setProgress({ phase: 'hashing', fraction: 0 });
    try {
      const uploaded = await uploadFolder(folder, setProgress, appId);
      onChange({ folder, uploaded });
      trackEvent(AmplitudeEvent.UPLOAD_FOLDER_SENT, {
        files: uploaded.fileCount,
        sent: uploaded.sentCount,
        existingApp: !!appId,
      });
      await onNext(uploaded);
    } catch (e) {
      console.error('upload failed', e);
      const code = e instanceof ApiV2Error ? e.code : 'UNKNOWN';
      trackEvent(AmplitudeEvent.UPLOAD_FOLDER_ERROR, { code });
      setError(
        e instanceof ApiV2Error && e.status > 0 && e.status < 500
          ? e.nextAction || e.message
          : 'The upload did not go through. Try again.'
      );
    } finally {
      setProgress(null);
    }
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div
      className={
        compact
          ? 'space-y-4'
          : 'px-[1.875rem] py-7 space-y-5 [@media(max-width:639.98px)]:px-4'
      }
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple
        onChange={onInputChange}
        {...directoryInputProps}
      />

      {!folder ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Drop your project folder here, or choose one"
          onClick={openPicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openPicker();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          className={`b4-focus flex flex-col items-center justify-center gap-3 text-center rounded-card border border-dashed px-6 py-12 cursor-pointer transition-colors duration-[120ms] ${
            isDragging
              ? 'border-action bg-action/[0.06]'
              : 'border-navy-400 hover:bg-navy-800'
          }`}
        >
          <span className={isDragging ? 'text-action' : 'text-navy-200'}>
            <UploadIcon size={28} />
          </span>
          <span className="text-ds-15 font-semibold text-white">
            {isReading ? 'Reading folder…' : 'Drag your project folder here'}
          </span>
          <span className="font-courrier text-ds-115 text-navy-200">
            or <span className="text-action-soft">choose a folder</span> from your computer
          </span>
          <span className="font-courrier text-ds-105 text-navy-300">
            node_modules, .git and .env files are left out
          </span>
        </div>
      ) : (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            if (!isUploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          className={`flex items-center gap-3.5 rounded-card border px-4 py-3.5 transition-colors duration-[120ms] ${
            isDragging ? 'border-action bg-action/[0.06]' : 'border-navy-500'
          }`}
        >
          <span className="w-9 flex-none flex items-center justify-center text-navy-200">
            <FolderIcon size={20} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-ds-135 font-semibold leading-[1.4] text-white truncate">
              {folder.name}
            </span>
            <span className="block font-courrier text-ds-115 leading-[1.4] mt-1 text-navy-200">
              {folder.files.length.toLocaleString()} {folder.files.length === 1 ? 'file' : 'files'} ·{' '}
              {formatBytes(folder.totalBytes)}
              {folder.ignoredCount ? ` · ${folder.ignoredCount.toLocaleString()} left out` : ''}
              {folder.files.some((entry) => entry.path === 'Dockerfile') ? ' · Dockerfile' : ''}
            </span>
          </span>
          {value?.uploaded ? (
            <span className="flex-none inline-flex items-center gap-1.5 font-courrier text-ds-105 uppercase tracking-[0.08em] text-state-ok">
              <CheckIcon size={13} /> uploaded
            </span>
          ) : null}
          <DsButton
            variant="ghost"
            size="md"
            isDisabled={isUploading}
            onClick={() => {
              setError(null);
              onChange(null);
            }}
          >
            Change
          </DsButton>
        </div>
      )}

      {isUploading && progress ? (
        <div className="space-y-2" aria-live="polite">
          <div className="flex justify-between font-courrier text-ds-115 text-navy-200">
            <span>{progressLabel(progress)}…</span>
            <span>{Math.round(progress.fraction * 100)}%</span>
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress.fraction * 100)}
            className="h-1.5 rounded-full bg-navy-600 overflow-hidden"
          >
            <div
              className="h-full bg-action transition-[width] duration-200"
              style={{ width: `${Math.round(progress.fraction * 100)}%` }}
            />
          </div>
        </div>
      ) : null}

      {problem || error ? (
        <div role="alert" className="flex items-start gap-2 text-ds-125 leading-[1.5]">
          <span className="w-[18px] h-[18px] flex-none mt-px rounded-full bg-state-err/[0.14] text-state-err flex items-center justify-center">
            <AlertIcon size={12} />
          </span>
          <div className="text-state-err">{problem || error}</div>
        </div>
      ) : null}

      <div className="flex justify-end pt-2">
        <DsButton
          variant="action"
          isDisabled={!folder || !!problem || isUploading || isReading || isContinuing}
          onClick={onClickNext}
        >
          {isUploading ? 'Uploading…' : nextLabel}
        </DsButton>
      </div>
    </div>
  );
};

export default UploadSource;
